import React from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ApplicationStatusBadge from "./ApplicationStatusBadge";
import {
  fetchTherapistApplicationDocumentLink,
  approveTherapistApplication,
  rejectTherapistApplication,
} from "../../../Services/therapistApplicationsService";

function InfoField({ label, value }) {
  return (
    <Box>
      <Typography sx={{ fontSize: "0.6875rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.8125rem", color: "#111827" }}>{value || "-"}</Typography>
    </Box>
  );
}

function formatDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function ApplicationDetailPane({ application, onStatusChange, onNotify }) {
  const [viewer, setViewer] = React.useState(null); // { url, name, contentType } | null
  const [loadingDoc, setLoadingDoc] = React.useState(null); // { appId, index, action: "view" | "newTab" } | null
  const [approveOpen, setApproveOpen] = React.useState(false);
  const [approveReason, setApproveReason] = React.useState("");
  const [rejectOpen, setRejectOpen] = React.useState(false);
  const [rejectReason, setRejectReason] = React.useState("");
  const [actionLoading, setActionLoading] = React.useState(false);

  React.useEffect(() => {
    setApproveOpen(false);
    setApproveReason("");
    setRejectOpen(false);
    setRejectReason("");
    setViewer(null);
  }, [application?.id]);

  if (!application) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#9ca3af",
          fontSize: "0.8125rem",
        }}
      >
        Select an application to view details
      </Box>
    );
  }

  // appId defaults to the current application but is passed explicitly for
  // documents belonging to a previous_applications history entry - each of
  // those has its own id and the document-link endpoint is keyed off it.
  const handleView = async (appId, index, doc) => {
    setLoadingDoc({ appId, index, action: "view" });
    try {
      const res = await fetchTherapistApplicationDocumentLink(appId, index);
      const url = res?.data?.url;
      if (url) setViewer({ url, name: doc.name, contentType: res?.data?.content_type || doc.content_type });
    } catch (err) {
      onNotify(err.message || "Couldn't open that document.", "error");
    } finally {
      setLoadingDoc(null);
    }
  };

  const handleOpenNewTab = async (appId, index) => {
    setLoadingDoc({ appId, index, action: "newTab" });
    try {
      const res = await fetchTherapistApplicationDocumentLink(appId, index);
      const url = res?.data?.url;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      onNotify(err.message || "Couldn't open that document.", "error");
    } finally {
      setLoadingDoc(null);
    }
  };

  // Re-approve covers both a previously-rejected and a previously-revoked
  // application - same approve endpoint, reason required either way.
  const isReactivating = application.status === "rejected" || application.status === "revoked";
  const wasRevoked = application.status === "revoked";
  const isRevoking = application.status === "approved";

  const handleApprove = async () => {
    if (isReactivating && !approveReason.trim()) return;
    setActionLoading(true);
    try {
      const res = await approveTherapistApplication(application.id, isReactivating ? approveReason.trim() : undefined);
      onStatusChange(application.id, {
        status: "approved",
        ...(isReactivating ? { reactivation_reason: approveReason.trim() } : {}),
      });
      onNotify(
        res?.data?.re_approved ? "Application re-approved — therapist access restored." : "Application approved.",
        "success"
      );
      setApproveOpen(false);
      setApproveReason("");
    } catch (err) {
      onNotify(err.message || "Couldn't approve this application.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setActionLoading(true);
    try {
      const res = await rejectTherapistApplication(application.id, rejectReason.trim());
      // The endpoint derives rejected vs revoked from the application's prior
      // status server-side - trust res.data.status over assuming "rejected".
      onStatusChange(application.id, {
        status: res?.data?.status || "rejected",
        rejection_reason: rejectReason.trim(),
      });
      onNotify(
        res?.data?.therapist_unverified
          ? "Access revoked — the therapist is no longer verified."
          : "Application rejected.",
        "success"
      );
      setRejectOpen(false);
      setRejectReason("");
    } catch (err) {
      onNotify(err.message || "Couldn't reject this application.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const isPending = application.status === "pending";
  const applicantName = application.user?.name || application.therapist?.therapist_name || "Unnamed applicant";
  const documents = application.documents || [];
  const previousApplications = application.previous_applications || [];
  const isImage = (viewer?.contentType || "").startsWith("image/");

  // Shared row renderer for a document list - used for the current
  // application and for each read-only history entry below. appId is
  // whichever application the document belongs to, since the link endpoint
  // is keyed off that application's own id, not the one on screen.
  const renderDocuments = (appId, docs) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      {docs.map((doc, index) => (
        <Box
          key={doc.s3_key || index}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            border: "1px solid #e5e7eb",
            borderRadius: 1.5,
            bgcolor: "#F9FAFB",
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 18, color: "#6b7280" }} />
          <Typography noWrap sx={{ flex: 1, fontSize: "0.8125rem", color: "#111827" }}>
            {doc.name}
          </Typography>
          <IconButton
            size="small"
            onClick={() => handleView(appId, index, doc)}
            disabled={Boolean(loadingDoc)}
            title="View"
          >
            {loadingDoc?.appId === appId && loadingDoc?.index === index && loadingDoc?.action === "view" ? (
              <CircularProgress size={16} />
            ) : (
              <VisibilityOutlinedIcon fontSize="small" sx={{ color: "#6b7280" }} />
            )}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleOpenNewTab(appId, index)}
            disabled={Boolean(loadingDoc)}
            title="Open in new tab"
          >
            {loadingDoc?.appId === appId && loadingDoc?.index === index && loadingDoc?.action === "newTab" ? (
              <CircularProgress size={16} />
            ) : (
              <OpenInNewIcon fontSize="small" sx={{ color: "#6b7280" }} />
            )}
          </IconButton>
        </Box>
      ))}
      {docs.length === 0 && (
        <Typography sx={{ fontSize: "0.8125rem", color: "#9ca3af" }}>No documents submitted.</Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <Box sx={{ p: 2.5, pb: 1.5 }}>
        {/* One unified card - applicant + practice info together */}
        <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 2.5, bgcolor: "#FFFFFF" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 0.5 }}>
            <Typography sx={{ fontSize: "1.0625rem", fontWeight: 600, color: "#111827" }}>{applicantName}</Typography>
            <ApplicationStatusBadge status={application.status} />
          </Box>
          <Typography sx={{ fontSize: "0.8125rem", color: "#6b7280", mb: 2 }}>{application.user?.email}</Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <InfoField label="Practice" value={application.practice_name} />
            <InfoField label="Practice email" value={application.practice_email} />
            <InfoField label="City" value={application.practice_city} />
            <InfoField label="Submitted" value={formatDate(application.submitted_at)} />
          </Box>

          {/* rejection_reason stays on the record even after a re-approval,
              so both reasons stay visible together for audit context. */}
          {application.rejection_reason && (
            <Alert severity={isRevoking ? "warning" : "error"} sx={{ mt: 2, fontSize: "0.8125rem" }}>
              <strong>Rejection reason:</strong> {application.rejection_reason}
            </Alert>
          )}
          {application.reactivation_reason && (
            <Alert severity="info" sx={{ mt: 1.5, fontSize: "0.8125rem" }}>
              <strong>Re-approval reason:</strong> {application.reactivation_reason}
            </Alert>
          )}
        </Box>
      </Box>

      <Box sx={{ px: 2.5, pb: 2.5 }}>
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#374151", textTransform: "uppercase", letterSpacing: "0.04em", mb: 1.5 }}>
          Documents
        </Typography>

        {renderDocuments(application.id, documents)}
      </Box>

      {previousApplications.length > 0 && (
        <Box sx={{ px: 2.5, pb: 2.5 }}>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#374151", textTransform: "uppercase", letterSpacing: "0.04em", mb: 1.5 }}>
            Application history ({previousApplications.length})
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {previousApplications.map((past) => (
              <Accordion
                key={past.id}
                disableGutters
                elevation={0}
                sx={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px !important",
                  bgcolor: "#F9FAFB",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, width: "100%", pr: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#111827" }}>
                        {past.practice_name || "-"}
                      </Typography>
                      <Typography noWrap sx={{ fontSize: "0.75rem", color: "#6b7280" }}>
                        Submitted {formatDate(past.submitted_at)}
                      </Typography>
                    </Box>
                    <ApplicationStatusBadge status={past.status} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
                    <InfoField label="Practice" value={past.practice_name} />
                    <InfoField label="Practice email" value={past.practice_email} />
                    <InfoField label="City" value={past.practice_city} />
                    <InfoField label="Reviewed" value={formatDate(past.reviewed_at)} />
                  </Box>

                  {past.rejection_reason && (
                    <Alert severity={past.status === "revoked" ? "warning" : "error"} sx={{ mb: 1.5, fontSize: "0.8125rem" }}>
                      <strong>{past.status === "revoked" ? "Revocation reason:" : "Rejection reason:"}</strong>{" "}
                      {past.rejection_reason}
                    </Alert>
                  )}
                  {past.reactivation_reason && (
                    <Alert severity="info" sx={{ mb: 1.5, fontSize: "0.8125rem" }}>
                      <strong>Re-approval reason:</strong> {past.reactivation_reason}
                    </Alert>
                  )}

                  <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", mb: 1 }}>
                    Documents
                  </Typography>
                  {renderDocuments(past.id, past.documents || [])}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
      )}
      </Box>

      {(isPending || isRevoking || isReactivating) && (
        <Box sx={{ p: 2, borderTop: "1px solid #e5e7eb", display: "flex", gap: 1.25, flexShrink: 0 }}>
          {(isPending || isRevoking) && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setRejectOpen(true)}
              sx={{ textTransform: "none", borderRadius: 1.5, flex: 1 }}
            >
              {isRevoking ? "Revoke access" : "Reject"}
            </Button>
          )}
          {(isPending || isReactivating) && (
            <Button
              variant="contained"
              onClick={() => setApproveOpen(true)}
              sx={{ textTransform: "none", borderRadius: 1.5, flex: 1, bgcolor: "#0058a5", "&:hover": { bgcolor: "#003d73" } }}
            >
              {isReactivating ? "Re-approve" : "Approve"}
            </Button>
          )}
        </Box>
      )}

      {/* In-app document viewer (same tab) - PDFs render via the browser's
          native iframe viewer, images render directly. Never cached: a
          fresh presigned link is fetched on every click. */}
      <Dialog open={Boolean(viewer)} onClose={() => setViewer(null)} maxWidth="md" fullWidth PaperProps={{ sx: { height: "85vh" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, py: 1.5 }}>
          <Typography noWrap sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>
            {viewer?.name}
          </Typography>
          <IconButton onClick={() => setViewer(null)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: "flex", bgcolor: "#f3f4f6" }}>
          {viewer &&
            (isImage ? (
              <Box
                component="img"
                src={viewer.url}
                alt={viewer.name}
                sx={{ maxWidth: "100%", maxHeight: "100%", m: "auto", objectFit: "contain" }}
              />
            ) : (
              <Box component="iframe" src={viewer.url} title={viewer.name} sx={{ flex: 1, border: "none" }} />
            ))}
        </DialogContent>
      </Dialog>

      {/* Approve confirmation - plain approve (pending) needs no reason;
          re-approving a rejected or revoked application requires one, since
          it's reversing a documented decision. */}
      <Dialog open={approveOpen} onClose={actionLoading ? undefined : () => setApproveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isReactivating ? "Re-approve application" : "Approve application"}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: isReactivating ? 2 : 0 }}>
            {isReactivating ? (
              <>
                Re-approve <strong>{applicantName}</strong>? This reverses the earlier {wasRevoked ? "revocation" : "rejection"} and
                restores their therapist role on next login. Tell them why the decision is being reversed - they'll
                receive this reason by email alongside the original {wasRevoked ? "revocation" : "rejection"} reason.
              </>
            ) : (
              <>
                Approve <strong>{applicantName}</strong>? This grants the therapist role on their next login and
                emails them the decision.
              </>
            )}
          </DialogContentText>
          {isReactivating && (
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={3}
              placeholder={`Reason for reversing the ${wasRevoked ? "revocation" : "rejection"}`}
              value={approveReason}
              onChange={(e) => setApproveReason(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
          <Button onClick={() => setApproveOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            disabled={actionLoading || (isReactivating && !approveReason.trim())}
            autoFocus={!isReactivating}
          >
            {actionLoading ? (isReactivating ? "Re-approving..." : "Approving...") : isReactivating ? "Re-approve" : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject with required reason - also used to revoke an already-approved
          (verified) therapist, which un-verifies their therapist row as a
          side effect (therapist_unverified in the response). */}
      <Dialog open={rejectOpen} onClose={actionLoading ? undefined : () => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isRevoking ? "Revoke therapist access" : "Reject application"}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {isRevoking ? (
              <>
                Revoke <strong>{applicantName}</strong>&apos;s verified therapist status? This immediately
                un-verifies their therapist account - existing patient records and lab tests stay intact, but they
                lose live access. Tell them why; they'll receive this reason by email.
              </>
            ) : (
              <>
                Tell <strong>{applicantName}</strong> why this application is being rejected. They'll receive this
                reason by email and can re-apply afterward.
              </>
            )}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            placeholder={isRevoking ? "Reason for revoking access" : "Reason for rejection"}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
          <Button onClick={() => setRejectOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            color="error"
            variant="contained"
            disabled={actionLoading || !rejectReason.trim()}
          >
            {actionLoading ? (isRevoking ? "Revoking..." : "Rejecting...") : isRevoking ? "Revoke access" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
